// Moteur audio (Tone.js) : musique (flipper sur le bandit manchot, boucle de fond dans le labyrinthe) + jingles de fin de
// niveau, volume réglable par crans.
// La création de l'audio (AudioContext) reste lazy : rien n'est créé avant le premier geste
// utilisateur (contrainte navigateur). Le CHARGEMENT du code de Tone.js, lui, est préchargé en
// arrière-plan (voir preload ci-dessous) dès que possible, sans attendre ce geste : sur iOS
// Safari, l'audio ne peut être débloqué que de façon synchrone dans le geste utilisateur, et un
// `import('tone')` encore en cours au moment du clic romprait ce lien — le son ne démarrerait
// alors jamais, silencieusement. Précharger permet à `init()` de résoudre l'import instantanément.
import { createSlotSounds } from './slotSounds.js';
import { createSlotMusic } from './slotMusic.js';

let tonePromise = null;
function loadTone() {
  if (!tonePromise) tonePromise = import('tone');
  return tonePromise;
}

// Crans de volume : 0 = coupé (silence total), 1..MAX = du plus étouffé au plus fort.
// Le cran maximal reproduit le volume fixe d'origine du jeu (-10dB), rien n'est plus fort qu'avant.
export const MAX_VOLUME_STEP = 5;
export const DEFAULT_VOLUME_STEP = MAX_VOLUME_STEP;
const MIN_AUDIBLE_DB = -30;
const MAX_DB = -10;

/** Fonction pure, testable indépendamment de Tone.js/Web Audio. */
export function stepToDb(step) {
  if (step <= 0) return -Infinity;
  const clamped = Math.min(step, MAX_VOLUME_STEP);
  const dbPerStep = (MAX_DB - MIN_AUDIBLE_DB) / (MAX_VOLUME_STEP - 1);
  return MIN_AUDIBLE_DB + (clamped - 1) * dbPerStep;
}

function clampStep(step) {
  return Math.max(0, Math.min(MAX_VOLUME_STEP, Math.round(step)));
}

export function createAudioEngine({ bpm = 120, initialVolumeStep = DEFAULT_VOLUME_STEP, initialMusicEnabled = true } = {}) {
  let Tone = null;
  let master = null; // Tone.Volume : le cran de volume s'applique à tout (musique et effets)
  let synth = null;
  let bgLoop = null;
  let slotSounds = null; // instruments du bandit manchot, créés à l'initialisation
  let slotMusic = null; // musique de flipper du bandit manchot (services/slotMusic.js)
  let musicScene = 'slot'; // 'slot' : le bandit est affiché (le jeu s'ouvre dessus) ; 'game' : le labyrinthe
  let initialized = false;
  let volumeStep = clampStep(initialVolumeStep);
  let musicEnabled = initialMusicEnabled;

  function applyVolume() {
    // Tous les instruments passent par ce nœud : ajouter un son (ex. le rat qui grignote) ne
    // demande pas de toucher au réglage du volume.
    if (master) master.volume.value = stepToDb(volumeStep);
  }

  /** À appeler dès le démarrage de l'appli, sans attendre : réchauffe le chunk Tone.js. */
  function preload() {
    loadTone().catch(() => {}); // une vraie erreur réapparaîtra normalement à l'usage réel (init)
  }

  // La musique démarre à l'initialisation seulement si elle est activée (voir setMusicEnabled) : celle du flipper sur le
  // bandit manchot, la boucle de fond dans le labyrinthe (setMusicScene). Les effets (playWin, playLose, ...) sont
  // déclenchés indépendamment de ces boucles, donc jamais coupés par le bouton musique on/off.
  async function init() {
    if (initialized) return;
    Tone = await loadTone();
    await Tone.start();
    master = new Tone.Volume(stepToDb(volumeStep)).toDestination();
    synth = new Tone.PolySynth(Tone.Synth).connect(master);
    slotSounds = createSlotSounds(Tone, master);
    slotMusic = createSlotMusic(Tone, master);
    Tone.getTransport().bpm.value = bpm;
    bgLoop = new Tone.Loop((time) => {
      synth.triggerAttackRelease(['C3'], '16n', time);
      synth.triggerAttackRelease(['G3'], '16n', time + Tone.Time('8n').toSeconds());
    }, '4n');
    Tone.getTransport().start();
    initialized = true;
    playScene();
  }

  /** Joue la musique de la scène courante (si la musique est activée), coupe l'autre. */
  function playScene() {
    if (!initialized) return;
    const slot = musicEnabled && musicScene === 'slot';
    const game = musicEnabled && musicScene === 'game';
    if (slot) slotMusic.start();
    else slotMusic.stop();
    if (game && bgLoop.state !== 'started') bgLoop.start(0);
    if (!game && bgLoop.state === 'started') bgLoop.stop();
  }

  /** Coupe ou relance la musique, sans toucher au volume ni aux effets. */
  function setMusicEnabled(enabled) {
    musicEnabled = enabled;
    playScene();
  }

  /**
   * Musique du moment : 'slot' (flipper, tant que le bandit manchot est affiché) ou 'game' (boucle du labyrinthe).
   * Retenue tant que l'audio n'est pas débloqué, jouée dès qu'il l'est.
   */
  function setMusicScene(scene) {
    if (scene === musicScene) return;
    musicScene = scene;
    playScene();
  }

  function getMusicEnabled() {
    return musicEnabled;
  }

  function setBpm(value) {
    bpm = value;
    if (initialized) Tone.getTransport().bpm.value = value;
  }

  /** Règle le volume par cran (0..MAX_VOLUME_STEP, borné). Retourne le cran réellement appliqué. */
  function setVolumeStep(step) {
    volumeStep = clampStep(step);
    applyVolume();
    return volumeStep;
  }

  function getVolumeStep() {
    return volumeStep;
  }

  function pause() {
    if (initialized) Tone.getTransport().pause();
  }

  function resume() {
    if (initialized) Tone.getTransport().start();
  }

  function play(notes) {
    if (!initialized) return;
    const now = Tone.now();
    for (const [note, duration, offset] of notes) {
      synth.triggerAttackRelease(note, duration, now + offset);
    }
  }

  // Bandit manchot (ui/slotMachine.js) : ses propres instruments (services/slotSounds.js), branchés sur le même nœud de
  // volume. Ce sont des effets : le bouton musique ne les coupe pas, le volume s'y applique comme au reste. Silencieux
  // tant que l'audio n'est pas débloqué.
  const playLever = () => slotSounds?.lever();
  /** @param {number[]} times instants des « tic » en ms depuis maintenant ; @param {number} durationMs durée de la roue */
  const playReelTicks = (times, durationMs) => slotSounds?.ticks(times, durationMs);
  const playReelStop = (index) => slotSounds?.stop(index);
  const playReveal = () => slotSounds?.reveal();
  const playStart = () => slotSounds?.start();
  const playCoin = () => slotSounds?.coin();

  const playWin = () => play([['C4', '8n', 0], ['E4', '8n', 0.15], ['G4', '8n', 0.3], ['C5', '2n', 0.45]]);
  const playSurvive = () => play([['C4', '8n', 0], ['E4', '4n', 0.2]]);
  const playLose = () => play([['C4', '8n', 0], ['Gb3', '8n', 0.2], ['Eb3', '2n', 0.4]]);

  return {
    preload, init, setBpm, setVolumeStep, getVolumeStep, setMusicEnabled, getMusicEnabled, setMusicScene, pause, resume,
    playWin, playSurvive, playLose,
    playLever, playReelTicks, playReelStop, playReveal, playStart, playCoin,
    get isInitialized() { return initialized; },
  };
}
